import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Table, Button, Modal, Tabs, Form, Input, Select, Space,
  Tag, Switch, Typography, Spin, message, Popconfirm, InputNumber,
  Row, Col, Empty, Radio, Tooltip, Progress,
} from 'antd';
import {
  EditOutlined, DeleteOutlined, PlayCircleOutlined,
  PauseCircleOutlined, SettingOutlined, UnorderedListOutlined,
  ArrowLeftOutlined, DownloadOutlined, UploadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { commerceServerTablePagination, COMMERCE_DEFAULT_PAGE_SIZE } from '../constants/commerceListPagination';
import { hasRole as checkRole } from '../utils/permissionUtils';
import commerceImportApi from '../services/commerceImportApi';
import CommerceImportFieldMapping from '../components/CommerceImportFieldMapping';
import { generateAutoMapping } from './CommerceImportPage';
import dayjs from 'dayjs';
import {
  buildCommerceAuditColumns,
  formatCommerceDateTime,
  useCommerceUserTimezone,
} from '../utils/commerceListDateUtils';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const TARGET_I18N_MAP = {
  customers: 'targetCustomers',
  priceCards: 'targetPriceCards',
  products: 'targetProducts',
  collections: 'targetCollections',
  collectionLinks: 'targetCollectionLinks',
  wpaGroupMembers: 'targetWpaGroupMembers',
  wpaGroupCustomers: 'targetWpaGroupCustomers',
  wpaContactOverrides: 'targetWpaContactOverrides',
  wpaIdentities: 'targetWpaIdentities',
};

const CommerceImportSchedulePage = () => {
  const { t } = useLanguage();
  const userTimezoneOffset = useCommerceUserTimezone();
  const navigate = useNavigate();
  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = checkRole(userInfo, 'Tenant_Admin');
  const [schedules, setSchedules] = useState([]);
  const [schedulesTotal, setSchedulesTotal] = useState(0);
  const [scheduleListPage, setScheduleListPage] = useState(1);
  const [scheduleListPageSize, setScheduleListPageSize] = useState(COMMERCE_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('basic');
  const [previewData, setPreviewData] = useState([]);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [sqlPreviewMeta, setSqlPreviewMeta] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [fieldMappingForm] = Form.useForm();
  const previewDataLoadedRef = useRef(false);
  const importMappingFileRef = useRef(null);
  const [executingId, setExecutingId] = useState(null);
  const commerceProgressPollRef = useRef(null);
  const commercePollingScheduleIdRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    isScheduled: true,
    scheduleType: 'interval',
    intervalMinutes: 60,
    status: 'Active',
    isActive: true,
    importType: 'excel',
    targetEntity: 'customers',
    sourceConfig: {},
    fieldMapping: {},
  });

  const tgt = useCallback((key) => t(`commerceImport.${TARGET_I18N_MAP[key]}`) || key, [t]);

  const getImportTypeTag = useCallback((type) => {
    const map = {
      excel: { color: 'green', label: t('contactImport.excelFile') },
      google: { color: 'blue', label: t('contactImport.googleSheets') },
      sql: { color: 'purple', label: t('contactImport.sqlDatabase') },
    };
    const cfg = map[type] || { color: 'default', label: type };
    return <Tag color={cfg.color}>{cfg.label}</Tag>;
  }, [t]);

  const getStatusTag = useCallback((status, isActive) => {
    if (!isActive) return <Tag color="orange">{t('commerceImport.schedulePause')}</Tag>;
    if (status === 'Active') return <Tag color="green">{t('commerceImport.scheduleActive')}</Tag>;
    return <Tag>{status}</Tag>;
  }, [t]);

  const loadSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await commerceImportApi.getSchedules({
        page: scheduleListPage,
        pageSize: scheduleListPageSize
      });
      if (res.success) {
        setSchedules(res.schedules || []);
        setSchedulesTotal(typeof res.total === 'number' ? res.total : (res.schedules || []).length);
      }
    } catch (e) {
      message.error(t('commerceImport.scheduleLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, scheduleListPage, scheduleListPageSize]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  useEffect(() => () => {
    if (commerceProgressPollRef.current) {
      clearInterval(commerceProgressPollRef.current);
      commerceProgressPollRef.current = null;
    }
  }, []);

  const stopCommerceProgressPoll = useCallback(() => {
    if (commerceProgressPollRef.current) {
      clearInterval(commerceProgressPollRef.current);
      commerceProgressPollRef.current = null;
    }
    commercePollingScheduleIdRef.current = null;
  }, []);

  const startCommerceProgressPoll = useCallback((scheduleId) => {
    stopCommerceProgressPoll();
    commercePollingScheduleIdRef.current = scheduleId;
    const tick = async () => {
      try {
        const r = await commerceImportApi.getScheduleImportProgress(scheduleId);
        if (!r?.success || !r.data) return;
        const d = r.data;
        setSchedules((prev) =>
          prev.map((s) =>
            s.id === scheduleId
              ? {
                  ...s,
                  importRunStatus: d.syncStatus,
                  importTotalRows: d.totalRecordsToSync,
                  importProcessedRows: d.recordsProcessed,
                  importOkCount: d.recordsInserted,
                  importFailCount: d.importFailCount,
                  importSkippedCount: d.recordsSkipped,
                  importStartedAt: d.syncStartedAt,
                  importFinishedAt: d.syncFinishedAt,
                  importErrorMessage: d.syncErrorMessage,
                }
              : s
          )
        );
        if (d.syncStatus === 'Completed' || d.syncStatus === 'Failed') {
          stopCommerceProgressPoll();
          if (d.syncStatus === 'Completed') {
            message.success(t('commerceImport.scheduleExecuteSuccess'));
          } else {
            message.error((d.syncErrorMessage || t('commerceImport.scheduleExecuteFailed')).toString().slice(0, 200));
          }
          loadSchedules();
        }
      } catch {
        /* 輪詢失敗時略過 */
      }
    };
    void tick();
    commerceProgressPollRef.current = setInterval(tick, 3000);
  }, [loadSchedules, stopCommerceProgressPoll, t]);

  useEffect(() => {
    const running = (schedules || []).find((s) => s.importRunStatus === 'Running');
    if (running) {
      if (commercePollingScheduleIdRef.current !== running.id) {
        startCommerceProgressPoll(running.id);
      }
      return;
    }
    if (commercePollingScheduleIdRef.current) {
      stopCommerceProgressPoll();
    }
  }, [schedules, startCommerceProgressPoll, stopCommerceProgressPoll]);

  useEffect(() => {
    if (editModalVisible && editingSchedule && formData.sourceConfig && Object.keys(formData.sourceConfig).length > 0) {
      if (!previewDataLoadedRef.current) {
        previewDataLoadedRef.current = true;
        const timer = setTimeout(() => loadPreviewData(), 300);
        return () => clearTimeout(timer);
      }
    }
      if (!editModalVisible) {
      previewDataLoadedRef.current = false;
      setPreviewData([]);
      setPreviewColumns([]);
      setSqlPreviewMeta(null);
    }
  }, [editModalVisible, editingSchedule?.id, formData.sourceConfig]);

  const loadPreviewData = async () => {
    if (!editingSchedule || !formData.sourceConfig) return;
    setLoadingPreview(true);
    try {
      const srcCfg = typeof formData.sourceConfig === 'string' ? JSON.parse(formData.sourceConfig) : { ...formData.sourceConfig };
      const type = editingSchedule.importType || formData.importType;
      if (type === 'excel' && !srcCfg.filePath) {
        message.warning(t('contactImport.pleaseUploadExcelFile'));
        return;
      }
      if (type === 'google' && !srcCfg.url && !srcCfg.spreadsheetId) {
        message.warning(t('contactImport.pleaseEnterGoogleSheetsUrl'));
        return;
      }
      if (type === 'sql' && (!srcCfg.server || !srcCfg.database || (!srcCfg.table && !srcCfg.query))) {
        message.warning(t('commerceImport.sqlConfigIncomplete'));
        return;
      }
      let result;
      if (type === 'excel') result = await commerceImportApi.loadFromExcel(srcCfg);
      else if (type === 'google') result = await commerceImportApi.loadFromGoogleDocs(srcCfg);
      else if (type === 'sql') result = await commerceImportApi.loadFromSql({ ...srcCfg, previewMode: true });
      if (result?.success) {
        const cols = (result.columns || []).map(c => ({ title: c, dataIndex: c, key: c }));
        setPreviewData(result.data || []);
        setPreviewColumns(cols);
        const autoMap = generateAutoMapping(formData.targetEntity, cols);
        // forceRender: true on the fieldMapping tab guarantees form fields are always mounted,
        // so getFieldsValue() reliably returns current values (including undefined for cleared fields).
        // Only apply auto-mapping to fields that are currently empty — never overwrite values the
        // user has already set (or deliberately cleared).
        const currentFormValues = fieldMappingForm.getFieldsValue();
        const autoMapFiltered = Object.fromEntries(
          Object.entries(autoMap).filter(([k]) => {
            const cur = currentFormValues[k];
            return cur === undefined || cur === null || cur === '';
          })
        );
        if (Object.keys(autoMapFiltered).length > 0) {
          fieldMappingForm.setFieldsValue(autoMapFiltered);
        }
        if (type === 'sql') {
          setSqlPreviewMeta({
            totalMatchingRows: result.totalMatchingRows,
            previewTopApplied: result.previewTopApplied,
            previewTopSkippedForWithCte: result.previewTopSkippedForWithCte,
          });
        } else {
          setSqlPreviewMeta(null);
        }
        message.success(t('commerceImport.parseSuccess').replace('{count}', (result.data || []).length));
      } else {
        message.warning(result?.message || t('commerceImport.schedulePreviewFailed'));
      }
    } catch (e) {
      message.warning(t('commerceImport.schedulePreviewFailedManual'));
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleReloadPreview = async () => {
    await loadPreviewData();
  };

  const handleExportMapping = () => {
    const rawMapping = fieldMappingForm.getFieldsValue();
    const mapping = Object.fromEntries(
      Object.entries(rawMapping).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const exportData = {
      targetEntity: formData.targetEntity,
      scheduleName: formData.name,
      fieldMapping: mapping,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `field-mapping-${formData.targetEntity}-${dayjs().format('YYYYMMDD-HHmmss')}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleImportMappingFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        const mapping = parsed.fieldMapping && typeof parsed.fieldMapping === 'object'
          ? parsed.fieldMapping
          : (typeof parsed === 'object' && parsed !== null ? parsed : null);
        if (mapping) {
          fieldMappingForm.setFieldsValue(mapping);
          message.success(t('commerceImport.mappingImportSuccess'));
        } else {
          message.error(t('commerceImport.mappingImportFailed'));
        }
      } catch {
        message.error(t('commerceImport.mappingImportFailed'));
      }
      e.target.value = '';
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleTestSql = async () => {
    const srcCfg = typeof formData.sourceConfig === 'string' ? JSON.parse(formData.sourceConfig) : formData.sourceConfig;
    try {
      const result = await commerceImportApi.testSqlConnection(srcCfg);
      if (result.success) message.success(t('contactImport.testConnection') + ' OK');
      else message.error(result.message || t('commerceImport.schedulePreviewFailed'));
    } catch (e) {
      message.error((e.response?.data?.message || e.message) || t('commerceImport.schedulePreviewFailed'));
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    let srcCfg = typeof schedule.sourceConfig === 'string' ? JSON.parse(schedule.sourceConfig) : (schedule.sourceConfig || {});
    if (!srcCfg || typeof srcCfg !== 'object') srcCfg = {};
    const it = schedule.importType || 'excel';
    if (it === 'sql') {
      srcCfg = {
        server: srcCfg.server || '',
        database: srcCfg.database || '',
        username: srcCfg.username || '',
        password: srcCfg.password || '',
        table: srcCfg.table || '',
        query: srcCfg.query || '',
      };
    } else if (it === 'excel') {
      srcCfg = {
        filePath: srcCfg.filePath || '',
        sheetName: srcCfg.sheetName || '',
        availableSheets: srcCfg.availableSheets || [],
      };
    } else if (it === 'google') {
      srcCfg = {
        url: srcCfg.url || '',
        sheetName: srcCfg.sheetName || '',
        spreadsheetId: srcCfg.spreadsheetId || '',
        fileType: srcCfg.fileType || '',
        availableSheets: srcCfg.availableSheets || [],
      };
    }
    const fldMap = typeof schedule.fieldMapping === 'string' ? JSON.parse(schedule.fieldMapping) : (schedule.fieldMapping || {});
    setFormData({ name: schedule.name, isScheduled: schedule.isScheduled ?? true, scheduleType: schedule.scheduleType || 'interval', intervalMinutes: schedule.intervalMinutes || 60, status: schedule.status, isActive: schedule.isActive, importType: it, targetEntity: schedule.targetEntity || 'customers', sourceConfig: srcCfg, fieldMapping: fldMap });
    fieldMappingForm.setFieldsValue(fldMap);
    setEditModalVisible(true);
  };

  const handleUpdateSchedule = async () => {
    try {
      try {
        await fieldMappingForm.validateFields();
      } catch (validateErr) {
        if (validateErr?.errorFields?.length) {
          message.warning(t('commerceImport.fieldMappingFormIncomplete'));
          return;
        }
        throw validateErr;
      }
      const rawMapping = fieldMappingForm.getFieldsValue();
      const savedMapping = formData.fieldMapping || {};
      // Fields with a value → normal mapping.
      // Fields previously in the saved mapping but now cleared (undefined) → save null,
      //   so the backend knows to clear that column in the DB on import.
      // Fields that were never in the mapping and are still empty → omit (don't touch DB).
      const mapping = {};
      for (const [k, v] of Object.entries(rawMapping)) {
        if (v !== undefined && v !== null && v !== '') {
          mapping[k] = v;
        } else if (k in savedMapping) {
          mapping[k] = null;
        }
      }
      const updateData = {
        name: formData.name,
        importType: formData.importType,
        targetEntity: formData.targetEntity,
        isScheduled: formData.isScheduled,
        scheduleType: formData.scheduleType,
        intervalMinutes: formData.scheduleType === 'interval' ? formData.intervalMinutes : null,
        scheduleCron: null,
        sourceConfig: formData.sourceConfig,
        fieldMapping: mapping,
        allowUpdateDuplicates: editingSchedule?.allowUpdateDuplicates || false,
        isActive: formData.isActive,
      };
      await commerceImportApi.updateSchedule(editingSchedule.id, updateData);
      message.success(t('commerceImport.scheduleUpdated'));
      setEditModalVisible(false);
      loadSchedules();
    } catch (e) {
      message.error(t('commerceImport.scheduleUpdateFailed') + ': ' + (e.response?.data?.message || e.message));
    }
  };

  const handleDelete = async (id) => {
    try {
      await commerceImportApi.deleteSchedule(id);
      message.success(t('commerceImport.scheduleDeleted'));
      loadSchedules();
    } catch (e) {
      message.error(t('commerceImport.scheduleDeleteFailed'));
    }
  };

  const handleToggleActive = async (schedule) => {
    try {
      await commerceImportApi.updateScheduleStatus(schedule.id, { isActive: !schedule.isActive });
      message.success(schedule.isActive ? t('commerceImport.schedulePaused') : t('commerceImport.scheduleResumed'));
      loadSchedules();
    } catch (e) {
      message.error(t('commerceImport.scheduleToggleFailed'));
    }
  };

  const handleExecute = async (schedule) => {
    setExecutingId(schedule.id);
    try {
      const res = await commerceImportApi.executeSchedule(schedule.id);
      if (res.success) {
        if (res.started) {
          message.success(res.message || t('commerceImport.importRunningBackground'));
          startCommerceProgressPoll(schedule.id);
          setSchedules((prev) =>
            prev.map((s) =>
              s.id === schedule.id
                ? {
                    ...s,
                    importRunStatus: 'Running',
                    importTotalRows: 0,
                    importProcessedRows: 0,
                    importOkCount: 0,
                    importFailCount: 0,
                    importSkippedCount: 0,
                  }
                : s
            )
          );
        } else {
          message.success(res.message || t('commerceImport.scheduleExecuteSuccess'));
          loadSchedules();
        }
      } else {
        message.error(res.message || t('commerceImport.scheduleExecuteFailed'));
      }
    } catch (e) {
      if (e.response?.status === 409) {
        message.warning(t('commerceImport.importAlreadyRunning'));
        startCommerceProgressPoll(schedule.id);
      } else {
        message.error(e.response?.data?.message || e.message || t('commerceImport.scheduleExecuteFailed'));
      }
    } finally {
      setExecutingId(null);
    }
  };

  const columns = [];
  if (isTenantAdmin) {
    columns.push({
      title: t('commerceImport.scheduleCompany'),
      dataIndex: 'companyName',
      key: 'companyName',
      width: 160,
      ellipsis: true,
      render: (v) => v || '-',
    });
  }
  columns.push(
    { title: t('commerceImport.scheduleName'), dataIndex: 'name', key: 'name', width: 200, ellipsis: true },
    {
      title: t('commerceImport.scheduleTarget'),
      dataIndex: 'targetEntity',
      key: 'targetEntity',
      width: 140,
      render: (v) => <Tag color="blue">{tgt(v)}</Tag>,
    },
    { title: t('contactImport.dataSourceType'), dataIndex: 'importType', key: 'importType', width: 120, render: getImportTypeTag },
    {
      title: t('commerceImport.scheduleFrequency'),
      key: 'frequency',
      width: 150,
      render: (_, r) => {
        if (!r.isScheduled) return '-';
        if (r.scheduleType === 'interval') return t('commerceImport.freqEveryNMin').replace('{n}', r.intervalMinutes);
        if (r.scheduleType === 'daily') return t('contactImport.daily');
        if (r.scheduleType === 'weekly') return t('contactImport.weekly');
        return r.scheduleType;
      },
    },
    {
      title: t('commerceImport.scheduleLastRun'),
      dataIndex: 'lastRunAt',
      key: 'lastRunAt',
      width: 180,
      render: (v) => (v ? formatCommerceDateTime(v, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: t('commerceImport.scheduleNextRun'),
      dataIndex: 'nextRunAt',
      key: 'nextRunAt',
      width: 180,
      render: (v) => (v ? formatCommerceDateTime(v, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: t('commerceImport.scheduleStatus'),
      key: 'status',
      width: 100,
      render: (_, r) => getStatusTag(r.status, r.isActive),
    },
    {
      title: t('commerceImport.importProgress'),
      key: 'importProgress',
      width: 260,
      render: (_, r) => {
        const st = r.importRunStatus || 'Idle';
        if (st === 'Running') {
          const total = r.importTotalRows || 0;
          const processed = r.importProcessedRows || 0;
          const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
          const ok = Number(r.importOkCount || 0);
          const fail = Number(r.importFailCount || 0);
          const skip = Number(r.importSkippedCount || 0);
          return (
            <div style={{ minWidth: 220 }}>
              <Progress percent={pct} size="small" status="active" />
              <div style={{ fontSize: 11, lineHeight: 1.2, marginTop: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, color: '#666' }}>
                  <span>{processed.toLocaleString()} / {total.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px' }}>
                  <span style={{ color: '#52c41a', fontWeight: 500 }}>OK {ok.toLocaleString()}</span>
                  {fail > 0 ? <span style={{ color: '#ff4d4f', fontWeight: 500 }}>失敗 {fail.toLocaleString()}</span> : null}
                  {skip > 0 ? <span style={{ color: '#faad14', fontWeight: 500 }}>略過 {skip.toLocaleString()}</span> : null}
                </div>
              </div>
            </div>
          );
        }
        if (st === 'Completed') {
          const ok = Number(r.importOkCount || 0);
          const fail = Number(r.importFailCount || 0);
          const skip = Number(r.importSkippedCount || 0);
          return (
            <div style={{ minWidth: 200 }}>
              <Progress percent={100} size="small" status="success" />
              <div style={{ fontSize: 11, lineHeight: 1.2, marginTop: 4 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px' }}>
                  <span style={{ color: '#52c41a', fontWeight: 500 }}>OK {ok.toLocaleString()}</span>
                  {fail > 0 ? <span style={{ color: '#ff4d4f', fontWeight: 500 }}>失敗 {fail.toLocaleString()}</span> : null}
                  {skip > 0 ? <span style={{ color: '#faad14', fontWeight: 500 }}>略過 {skip.toLocaleString()}</span> : null}
                </div>
              </div>
            </div>
          );
        }
        if (st === 'Failed') {
          return (
            <div style={{ minWidth: 200 }}>
              <Progress percent={0} size="small" status="exception" />
              {r.importErrorMessage ? (
                <Tooltip title={r.importErrorMessage}>
                  <div style={{ fontSize: 11, color: '#ff4d4f', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.importErrorMessage.length > 40 ? `${r.importErrorMessage.slice(0, 40)}…` : r.importErrorMessage}
                  </div>
                </Tooltip>
              ) : null}
            </div>
          );
        }
        return <span style={{ fontSize: 12, color: '#999' }}>—</span>;
      },
    },
    ...buildCommerceAuditColumns(t, userTimezoneOffset),
    {
      title: t('commerceImport.scheduleActions'),
      key: 'actions',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title={t('commerceImport.scheduleExecuteNow')}>
            <Button
              type="link"
              size="small"
              icon={<PlayCircleOutlined />}
              loading={executingId === record.id}
              onClick={() => handleExecute(record)}
            />
          </Tooltip>
          <Tooltip title={t('commerceImport.scheduleEdit')}>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title={record.isActive ? t('commerceImport.schedulePause') : t('commerceImport.scheduleResume')}>
            <Button
              type="link"
              size="small"
              icon={record.isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={() => handleToggleActive(record)}
            />
          </Tooltip>
          <Popconfirm title={t('commerceImport.scheduleDeleteConfirm')} onConfirm={() => handleDelete(record.id)}>
            <Tooltip title={t('commerceImport.scheduleDelete')}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  );

  const renderEditModal = () => (
    <Modal
      open={editModalVisible}
      title={t('commerceImport.scheduleEditTitle')}
      onOk={handleUpdateSchedule}
      onCancel={() => {
        setEditModalVisible(false);
        setPreviewData([]);
        setPreviewColumns([]);
        fieldMappingForm.resetFields();
        previewDataLoadedRef.current = false;
        setActiveTabKey('basic');
      }}
      width="95vw"
      style={{ top: 20 }}
      bodyStyle={{ height: 'calc(100vh - 180px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      {editingSchedule && (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Tabs
            activeKey={activeTabKey}
            onChange={setActiveTabKey}
            destroyInactiveTabPane
            items={[
              {
                key: 'basic',
                label: <span><SettingOutlined /> {t('commerceImport.scheduleBasicSettings')}</span>,
                children: (
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form layout="vertical">
                          <Form.Item label={t('commerceImport.scheduleName')}>
                            <Input value={formData.name} onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))} />
                          </Form.Item>
                          <Form.Item label={t('commerceImport.scheduleTarget')}>
                            <Tag color="blue">{tgt(formData.targetEntity)}</Tag>
                          </Form.Item>
                          <Form.Item label={t('contactImport.dataSourceType')}>
                            {getImportTypeTag(formData.importType)}
                          </Form.Item>
                          <Form.Item label={t('commerceImport.scheduleFrequency')}>
                            <Radio.Group value={formData.scheduleType} onChange={e => setFormData(prev => ({ ...prev, scheduleType: e.target.value }))}>
                              <Radio value="interval">{t('contactImport.everyXMinutes')}</Radio>
                              <Radio value="daily">{t('contactImport.daily')}</Radio>
                              <Radio value="weekly">{t('contactImport.weekly')}</Radio>
                            </Radio.Group>
                          </Form.Item>
                          {formData.scheduleType === 'interval' && (
                            <Form.Item label={t('commerceImport.scheduleIntervalLabel')}>
                              <InputNumber min={1} value={formData.intervalMinutes} onChange={v => setFormData(prev => ({ ...prev, intervalMinutes: v }))} />
                            </Form.Item>
                          )}
                          <Form.Item label={t('commerceImport.scheduleEnable')}>
                            <Switch checked={formData.isActive} onChange={v => setFormData(prev => ({ ...prev, isActive: v }))} />
                          </Form.Item>
                        </Form>
                      </Col>
                      <Col span={12}>
                        <Card title={t('commerceImport.scheduleSourceConfig')} size="small">
                          {formData.importType === 'excel' && formData.sourceConfig && (
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              <div>
                                <Text strong>{t('contactImport.excelFilePath')}：</Text>
                                <Input
                                  value={formData.sourceConfig.filePath || ''}
                                  onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    sourceConfig: { ...prev.sourceConfig, filePath: e.target.value },
                                  }))}
                                  placeholder="/Uploads/excel/example.xlsx"
                                  style={{ marginTop: 8 }}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.sheetName')}：</Text>
                                <Input
                                  value={formData.sourceConfig.sheetName || ''}
                                  onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    sourceConfig: { ...prev.sourceConfig, sheetName: e.target.value },
                                  }))}
                                  placeholder="Sheet1"
                                  style={{ width: '100%', marginTop: 8 }}
                                />
                              </div>
                            </Space>
                          )}
                          {formData.importType === 'google' && formData.sourceConfig && (
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              <div>
                                <Text strong>{t('contactImport.googleDocsUrl')}：</Text>
                                <Input
                                  value={formData.sourceConfig.url || ''}
                                  onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    sourceConfig: { ...prev.sourceConfig, url: e.target.value },
                                  }))}
                                  placeholder="https://docs.google.com/spreadsheets/d/..."
                                  style={{ marginTop: 8 }}
                                />
                              </div>
                              <div>
                                <Text strong>{t('commerceImport.googleSpreadsheetId')}：</Text>
                                <Input
                                  value={formData.sourceConfig.spreadsheetId || ''}
                                  onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    sourceConfig: { ...prev.sourceConfig, spreadsheetId: e.target.value },
                                  }))}
                                  placeholder={t('commerceImport.googleSpreadsheetIdPlaceholder')}
                                  style={{ width: '100%', marginTop: 8 }}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.sheetName')}：</Text>
                                <Input
                                  value={formData.sourceConfig.sheetName || ''}
                                  onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    sourceConfig: { ...prev.sourceConfig, sheetName: e.target.value },
                                  }))}
                                  placeholder="Sheet1"
                                  style={{ width: '100%', marginTop: 8 }}
                                />
                              </div>
                            </Space>
                          )}
                          {formData.importType === 'sql' && formData.sourceConfig && (
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              <div>
                                <Text strong>{t('contactImport.server')}：</Text>
                                <Input
                                  value={formData.sourceConfig.server || ''}
                                  onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    sourceConfig: { ...prev.sourceConfig, server: e.target.value },
                                  }))}
                                  placeholder="localhost"
                                  style={{ width: '100%', marginTop: 8 }}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.database')}：</Text>
                                <Input
                                  value={formData.sourceConfig.database || ''}
                                  onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    sourceConfig: { ...prev.sourceConfig, database: e.target.value },
                                  }))}
                                  style={{ width: '100%', marginTop: 8 }}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.username')}：</Text>
                                <Input
                                  value={formData.sourceConfig.username || ''}
                                  onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    sourceConfig: { ...prev.sourceConfig, username: e.target.value },
                                  }))}
                                  style={{ width: '100%', marginTop: 8 }}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.password')}：</Text>
                                <Input.Password
                                  value={formData.sourceConfig.password || ''}
                                  onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    sourceConfig: { ...prev.sourceConfig, password: e.target.value },
                                  }))}
                                  style={{ width: '100%', marginTop: 8 }}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.tableName')}：</Text>
                                <Input
                                  value={formData.sourceConfig.table || ''}
                                  onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    sourceConfig: { ...prev.sourceConfig, table: e.target.value },
                                  }))}
                                  style={{ width: '100%', marginTop: 8 }}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.customQuery')}：</Text>
                                <TextArea
                                  value={formData.sourceConfig.query || ''}
                                  onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    sourceConfig: { ...prev.sourceConfig, query: e.target.value },
                                  }))}
                                  rows={3}
                                  style={{ width: '100%', marginTop: 8 }}
                                />
                              </div>
                              <Space wrap>
                                <Button onClick={handleTestSql}>{t('contactImport.testConnection')}</Button>
                                <Button type="primary" onClick={handleReloadPreview} loading={loadingPreview}>
                                  {t('commerceImport.reloadPreview')}
                                </Button>
                              </Space>
                            </Space>
                          )}
                          {(formData.importType === 'excel' || formData.importType === 'google') && (
                            <div style={{ marginTop: 12 }}>
                              <Button type="primary" onClick={handleReloadPreview} loading={loadingPreview}>
                                {t('commerceImport.reloadPreview')}
                              </Button>
                            </div>
                          )}
                        </Card>
                        {editingSchedule?.lastRunAt && (
                          <div style={{ marginTop: 12 }}>
                            <Text type="secondary">
                              {t('commerceImport.scheduleLastRun')}：{formatCommerceDateTime(editingSchedule.lastRunAt, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss')}
                            </Text>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                ),
              },
              {
                key: 'fieldMapping',
                label: <span><UnorderedListOutlined /> {t('contactImport.fieldMapping')}</span>,
                forceRender: true,
                children: (
                  <Spin spinning={loadingPreview}>
                    <Row gutter={16} style={{ height: 'calc(100vh - 340px)' }}>
                      <Col span={12} style={{ height: '100%', overflow: 'auto' }}>
                        <Card title={t('contactImport.dataPreview')} size="small" bodyStyle={{ padding: 0 }}>
                          {(formData.importType || editingSchedule?.importType) === 'sql' && sqlPreviewMeta && (
                            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                              {sqlPreviewMeta.totalMatchingRows != null && (
                                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                  {t('commerceImport.sqlPreviewTotalRows').replace('{count}', String(sqlPreviewMeta.totalMatchingRows))}
                                </Text>
                              )}
                              {sqlPreviewMeta.previewTopApplied != null && (
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                                  {t('commerceImport.sqlPreviewTopNote').replace('{n}', String(sqlPreviewMeta.previewTopApplied))}
                                </Text>
                              )}
                              {sqlPreviewMeta.previewTopSkippedForWithCte && (
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                                  {t('commerceImport.sqlPreviewWithCteNote')}
                                </Text>
                              )}
                            </div>
                          )}
                          {previewColumns.length > 0 && previewData.length > 0 ? (
                            <Table dataSource={previewData.slice(0, 50)} columns={previewColumns.map(c => ({ ...c, width: 120, ellipsis: true }))} scroll={{ x: 'max-content', y: 300 }} size="small" pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100], size: 'small' }} rowKey={(_, i) => i} />
                          ) : (
                            <Empty description={t('commerceImport.noPreviewData')} style={{ padding: 24 }} />
                          )}
                        </Card>
                      </Col>
                      <Col span={12} style={{ height: '100%', overflow: 'auto' }}>
                        <Card
                          title={t('contactImport.fieldMapping')}
                          size="small"
                          extra={
                            <Space size={4}>
                              <Tooltip title={t('commerceImport.mappingExport')}>
                                <Button
                                  size="small"
                                  icon={<DownloadOutlined />}
                                  onClick={handleExportMapping}
                                />
                              </Tooltip>
                              <Tooltip title={t('commerceImport.mappingImport')}>
                                <Button
                                  size="small"
                                  icon={<UploadOutlined />}
                                  onClick={() => importMappingFileRef.current?.click()}
                                />
                              </Tooltip>
                              <input
                                ref={importMappingFileRef}
                                type="file"
                                accept=".json"
                                style={{ display: 'none' }}
                                onChange={handleImportMappingFile}
                              />
                            </Space>
                          }
                        >
                          <CommerceImportFieldMapping form={fieldMappingForm} columns={previewColumns} targetEntity={formData.targetEntity} />
                        </Card>
                      </Col>
                    </Row>
                  </Spin>
                ),
              },
            ]}
          />
        </div>
      )}
    </Modal>
  );

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            type="primary"
            shape="square"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/commerce/import')}
            style={{
              width: 40,
              height: 40,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </Col>
        <Col>
          <Title level={2} style={{ margin: 0, textAlign: 'right' }}>
            {t('commerceImport.scheduleTitle')}
          </Title>
        </Col>
      </Row>
      <Table
        dataSource={schedules}
        columns={columns}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={commerceServerTablePagination({
          page: scheduleListPage,
          pageSize: scheduleListPageSize,
          total: schedulesTotal,
          showTotal: (total) => t('commerceImport.scheduleTotalCount').replace('{count}', String(total)),
          onChange: (p, ps) => {
            setScheduleListPage(p);
            setScheduleListPageSize(ps);
          }
        })}
      />
      {renderEditModal()}
    </div>
  );
};

export default CommerceImportSchedulePage;
